import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguageStore, translations, type Language } from "../store/languageStore";
import { useMatchStore } from "../store/matchStore";
import { useNeedStore } from "../store/needStore";
import StepIndicator from "../components/StepIndicator";
import { motion } from "framer-motion";
import ResourceCard from "../components/ResourceCard";

const steps = [
  {
    number: 1,
    label: translations.stepLabels.selectNeed,
  },
  {
    number: 2,
    label: translations.stepLabels.matchSupport,
  },
  {
    number: 3,
    label: translations.stepLabels.exploreAid,
  },
];

// Helper function to get translation from multilingual object
const getTranslation = (translationObj: Record<Language, string> | string, language: Language): string => {
  if (typeof translationObj === 'string') {
    return translationObj;
  }
  return translationObj[language] || translationObj['EN'] || '';
};

const Results: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLanguage } = useLanguageStore();
  const { matchResults } = useMatchStore();
  const { selectedNeed } = useNeedStore();

  const lang = selectedLanguage || 'EN'; 
  
  useEffect(() => {
    if (!matchResults || !matchResults.length) {
      navigate('/match');
    }
  }, [matchResults, navigate]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  // Add safety check to prevent rendering if matchResults is undefined
  if (!matchResults) {
    return null;
  }

  // Sort results by match percentage (highest first)
  const sortedResults = [...matchResults].sort((a, b) => b.matchPercentage - a.matchPercentage);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 pt-24 pb-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-8"
        >
          <motion.div variants={itemVariants} className="mt-48 sm:mt-20">
            <StepIndicator steps={steps} currentStep={3} />
          </motion.div>

          <motion.div variants={itemVariants} className="flex items-center justify-between mt-32 sm:mt-12 mb-6">
            <button
              onClick={() => navigate('/match')}
              className="flex items-center text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {translations?.common?.back?.[lang] ?? 'Back'}
            </button>
          </motion.div>

          <motion.div variants={itemVariants} className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
              {translations.results.title[lang]}
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              {translations.results.subtitle[lang]}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              {translations.results.found[lang].replace('{count}', sortedResults.length.toString())}
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="grid gap-6">
            {sortedResults.map((program) => (
              <ResourceCard
                key={program.id}
                id={Number(program.id)}
                type={program.type as 'rent' | 'food' | 'shelter' | 'legal' | 'jobs'}
                name={getTranslation(program.name, lang)}
                description={getTranslation(program.description, lang)}
                matchPercentage={program.matchPercentage}
              />
            ))}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Results;